import { useCallback, useContext, useEffect, useRef, useState } from "react";
import CanvasDraw from "react-canvas-draw";

import ModalTemplate from "../../../common/modal/modalTemplate";
import BtnSpinner from "../../../common/btnSpinner";
import { fetchRequestPreSignSupport, submitSignature } from "../../currentJob/service";
import useCustomNavigate from "../../../common/hooks/useCustomNavigate";
import ListenerContext from "../../../listener/context";
import { openJobChatView } from "../../../App/service";

const SignaturePad = ({ canvasRef, onChange }) => {
    const wrapperRef = useRef(null);

    const [canvasWidth, setCanvasWidth] = useState(300);

    useEffect(() => {
        const updateWidth = () => {
            if (wrapperRef.current) {
                setCanvasWidth(wrapperRef.current.offsetWidth);
            }
        };

        updateWidth();

        window.addEventListener("resize", updateWidth);

        return () => {
            window.removeEventListener("resize", updateWidth);
        };
    }, []);

    return (
        <div
            ref={wrapperRef}
            className="border border-light overflow-hidden bg-white"
            style={{
                height: 260,
                borderRadius: 10,
            }}
        >
            <CanvasDraw
                ref={canvasRef}
                brushColor="#000"
                brushRadius={2}
                lazyRadius={1}
                hideGrid
                canvasWidth={canvasWidth}
                canvasHeight={260}
                onChange={onChange}
                style={{
                    touchAction: "none",
                }}
            />
        </div>
    );
};

const SignaturePadModal = ({
    show,
    job_id,
    closeModal,
    refresh = () => {},
}) => {
    const { dispatch } = useContext(ListenerContext);
    const { navigateToCustom } = useCustomNavigate();

    const canvasRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [btnWaiting, setBtnWaiting] = useState(false);
    const [description, setDescription] = useState("");

    useEffect(() => {
        if (show) {
            setHasSignature(false);
            setDescription("");
        }
    }, [show]);

    const handleSignatureChange = useCallback((canvas) => {
        try {
            const saveData = JSON.parse(canvas.getSaveData());
            setHasSignature(saveData.lines.length > 0);
        } catch (error) {
            setHasSignature(false);
        }
    }, []);

    const clearCanvas = () => {
        if (canvasRef.current) {
            canvasRef.current.clear();
            setHasSignature(false);
        }
    };

    const uploadSignature = async () => {
        if (!hasSignature) return;

        try {
            if (canvasRef.current) {
                const canvas = canvasRef.current.canvas.drawing;

                canvas.toBlob(async (blob) => {
                    if (!blob) return;

                    const file = new File([blob], "signature.png", {
                        type: "image/png",
                    });

                    setLoading(true);

                    const response = await submitSignature(job_id, file, description.trim());

                    if (response?.status === "Success") {
                        refresh();
                        closeModal();

                        navigateToCustom(`/jobs/details/${job_id}`);
                    }

                    setLoading(false);
                });
            }
        } catch (error) {
            setLoading(false);
        }
    };

    const requestPreSignSupport = async () => {
        try{
            setBtnWaiting(true);
            const response = await fetchRequestPreSignSupport(job_id);       
            if(response?.status === "Success") {
                // Handle success
                await openJobChatView(
                    job_id,
                    dispatch
                );
            }
        }catch(error){
            console.error("Error requesting pre-sign support:", error);
        } finally {
            setBtnWaiting(false);
            closeModal();
        }
    }

    return (
        <ModalTemplate
            show={show}
            closeFun={closeModal}
            centered
            title={null}
            body={
                <div className="p-2">
                    {/* Title */}
                    <h5 className="fw-bold text-dark mb-0">
                        Customer Signature
                    </h5>

                    {/* Description */}
                    <p
                        className="text-secondary mb-4"
                        style={{
                            fontSize: "1.0rem",
                        }}
                    >
                        Please sign to confirm the work is complete.
                    </p>

                    <div className="mb-3">
                        <label htmlFor="signature-description" className="form-label fw-semibold text-dark">
                            Review <span className="text-secondary fw-normal">(optional)</span>
                        </label>
                        <textarea
                            id="signature-description"
                            className="form-control"
                            rows="3"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            placeholder="Add any notes about the completed work"
                            disabled={loading}
                            style={{
                                backgroundColor: '#fff',
                                padding: '10px'
                            }}
                        />
                    </div>

                    {/* Signature Pad */}
                    <div className="position-relative">
                        {/* Clear Button */}
                        <button
                            type="button"
                            onClick={clearCanvas}
                            disabled={loading}
                            className="btn btn-link text-secondary text-decoration-underline p-0 position-absolute"
                            style={{
                                top: 15,
                                right: 20,
                                zIndex: 10,
                            }}
                        >
                            Clear Signature
                        </button>

                        <SignaturePad
                            canvasRef={canvasRef}
                            onChange={handleSignatureChange}
                        />
                    </div>

                    {/* Submit Button */}
                    <button
                        type="button"
                        onClick={uploadSignature}
                        disabled={loading || !hasSignature}
                        className="btn btn-success rounded-3 w-100 btn-sm mt-4 py-2 fw-bold shadow-sm"
                        style={{
                            fontSize: "0.85rem",
                        }}
                    >
                        {loading ? (
                            <BtnSpinner />
                        ) : (
                            "Submit Signature"
                        )}
                    </button>

                    {/* Footer Text */}
                    <p
                        className="text-secondary fst-italic mt-3 mb-0"
                        style={{
                            fontSize: "0.95rem",
                        }}
                    >
                        Please sign only if you are satisfied the work is
                        complete. Signing confirms completion and authorizes
                        payment.
                    </p>

                    {/* Help Link */}
                    <div className="text-center mt-4">
                        <button
                            type="button"
                            className="btn btn-link text-dark text-decoration-underline p-0"
                            onClick={requestPreSignSupport}
                            disabled={btnWaiting}
                        >
                           {btnWaiting ? <BtnSpinner /> : "⚠ Need help before signing?"}
                        </button>
                    </div>
                </div>
            }
        />
    );
};

export default SignaturePadModal;
